import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TableConfig } from "@rentablo/components";
import { ApiTokenResponse } from "@rentablo/api/apiv1";
import DeleteIcon from "@icons/myicons/trash-bin-delete.svg?react";
import { formatDateDDMMYYYY } from "@helpers";
import { PermissionBadges } from "./PermissionBadges";
import { TokenExpiryCell } from "./TokenExpiryCell";
import { formatLastUsed } from "./helpers";

export const useTableConfig = ({
  onRevoke,
}: {
  onRevoke: (token: ApiTokenResponse) => void;
}) => {
  const { t } = useTranslation();

  return {
    id: "api-tokens",
    getRowId: (row) => row.id,
    columns: [
      {
        id: "id",
        title: t("settings.apiAccess.table.id"),
        align: "left",
        renderer: ({ row }) => (
          <Typography variant="body3">{row.id}</Typography>
        ),
      },
      {
        id: "name",
        title: t("settings.apiAccess.table.name"),
        align: "left",
        renderer: ({ row }) => (
          <Box display="flex" flexDirection="column">
            <Typography variant="body3" noWrap>
              {row.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t("settings.apiAccess.table.createdAt", {
                date: formatDateDDMMYYYY(row.createdAt),
              })}
            </Typography>
          </Box>
        ),
      },
      {
        id: "permissions",
        title: t("settings.apiAccess.table.permissions"),
        align: "left",
        renderer: ({ row }) => (
          <PermissionBadges permissions={row.permissions ?? []} />
        ),
      },
      {
        id: "lastUsed",
        title: t("settings.apiAccess.table.lastUsed"),
        align: "left",
        renderer: ({ row }) => (
          <Typography variant="body3" color="textSecondary">
            {formatLastUsed(t, row.lastUsedAt)}
          </Typography>
        ),
      },
      {
        id: "expires",
        title: t("settings.apiAccess.table.expires"),
        align: "left",
        renderer: ({ row }) => <TokenExpiryCell expiresAt={row.expiresAt} />,
      },
      {
        id: "actions",
        title: "",
        align: "right",
        renderer: ({ row }) => (
          <Box display="flex" justifyContent="flex-end">
            <IconButton
              size="small"
              aria-label={t("settings.apiAccess.table.revokeAction")}
              title={t("settings.apiAccess.table.revokeAction")}
              data-testid="api-token-revoke-button"
              onClick={() => onRevoke(row)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ),
      },
    ],
  } satisfies TableConfig<ApiTokenResponse>;
};
